import React, { useEffect } from "react";
import { TextField, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from "../../hooks";
import { authAction } from "../../slices/authSlice";
import { LoadingButton } from "@mui/lab";
import SendIcon from '@mui/icons-material/Send';

const validationSchema = yup.object().shape({
  new_password: yup.string().min(8, "Password must be at least 8 characters long").required("New password is required"),
  confirm_password: yup.string().oneOf([yup.ref('new_password')], "Passwords don't match").required("Confirm password is required"),
});

type FormValues = {
  new_password: string;
  confirm_password: string;
};

const ResetPassword: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { verifyToken } = useParams<{ verifyToken: string }>();
  const { control, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  
  const { loading } = useAppSelector(state => state.auth);

  useEffect(() => {
    if (verifyToken) {
      dispatch(authAction({
        actionType: 'verify-token',
        method: 'get',
        data: { token: verifyToken },
      }))
      .unwrap()
      .catch(() => {
        navigate('/login')
      });
    }
  }, [verifyToken, dispatch, navigate]);

  const handleResetPassword = async (data: FormValues) => {
    if (verifyToken) {
      try {
        await dispatch(authAction({
          actionType: 'reset-password',
          method: 'put',
          data: {
            password: data.new_password,
            password_confirmation: data.confirm_password,
            token: verifyToken,
          },
        })).unwrap();
        
        navigate('/login');
      } catch (error) {
        console.error('Reset password failed:', error);
      }
    }
  };

  return (
    <>
      <Typography component="h1" variant="h5" gutterBottom>
        Reset Password
      </Typography>
      <form onSubmit={handleSubmit(handleResetPassword)} autoComplete="off" style={{ width: '100%' }}>
        <Controller
          name="new_password"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              variant="standard"
              margin="normal"
              fullWidth
              id="new_password"
              label="New password"
              type="password"
              autoFocus
              error={!!errors.new_password}
              helperText={errors.new_password?.message}
            />
          )}
        />
        <Controller
          name="confirm_password"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              variant="standard"
              margin="normal"
              fullWidth
              id="confirm_password"
              label="Confirm password"
              type="password"
              autoFocus
              error={!!errors.confirm_password}
              helperText={errors.confirm_password?.message}
            />
          )}
        />
        {
          loading ?
          <LoadingButton
            fullWidth
            endIcon={<SendIcon />}
            loading={loading}
            loadingPosition="end"
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Loading
          </LoadingButton>
          :
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Update password
          </Button>
        }
      </form>
      <Link to="/login" style={{ textDecoration: 'none' }}>
        <Typography sx={{ fontSize: '12px', my: 3, color: theme.palette.primary.main }}>
          Back to Login
        </Typography>
      </Link>
    </>
  );
};

export default ResetPassword;
